// ============================================
// pages/NotificationDetail.tsx
// 单条消息详情页（路由 /notifications/:id）
//   - 优先用列表跳转时带过来的 state.notification；
//     直接访问 URL 时回退到 /notifications/list 接口里按 id 找
//   - 告知为主：展示标题/内容/时间
//   - 提供「可选入口」让用户自己决定去不去：
//       · 有 project_short_id → 「查看作品」
//       · app 类（订单等）→ 「请打开 HiPiiDo Studio 查看 xxx」提示
//   - 顶部「← 返回」回到 /notifications 列表
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Bell, ArrowLeft, ExternalLink, User, Smartphone, Trash2 } from 'lucide-react';
import { getToken } from '../services/api';
import Layout from '../components/Layout';

const PUSH_BASE = 'https://push.hipiido.cloud';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  payload: Record<string, unknown>;
  read: boolean;
  created_at: number;
}

export default function NotificationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  // 列表跳转时带来的整条数据（最快路径）
  const passed = (location.state as any)?.notification as NotificationItem | undefined;

  const [item, setItem] = useState<NotificationItem | null>(passed || null);
  const [loading, setLoading] = useState(!passed);

  // 没有 state（比如直接刷新/访问 URL）→ 从列表接口按 id 找
  useEffect(() => {
    if (item) return;
    const token = getToken();
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const resp = await fetch(`${PUSH_BASE}/notifications/list?token=${encodeURIComponent(token)}`);
        const data = await resp.json();
        if (data && data.ok) {
          const found = (data.list || []).find((n: NotificationItem) => n.id === id);
          if (found) setItem(found);
        }
      } catch (e) { /* 找不到就显示未找到 */ }
      setLoading(false);
    })();
  }, [item, id]);

  const goBack = () => navigate('/notifications');

  // 删除这条（软删除）后返回列表
  const onDelete = async () => {
    const token = getToken();
    if (token && item) {
      try {
        await fetch(`${PUSH_BASE}/notifications/hide`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, notification_id: item.id }),
        });
      } catch (e) { /* 失败也返回 */ }
    }
    navigate('/notifications');
  };

  if (!getToken()) {
    return (
      <Layout width="narrow">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center text-ink-400">请先登录</div>
      </Layout>
    );
  }

  const payload = (item && item.payload) || {};
  const link = (payload.link as any) || null;
  const isApp = link && link.kind === 'app';
  const projectShortId = payload.project_short_id as string | undefined;
  const appHint = isApp && link.app_hint ? String(link.app_hint) : '查看相关操作';

  return (
    <Layout width="narrow">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* 顶部返回 */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={goBack}
            className="grid place-items-center w-9 h-9 rounded-full hover:bg-ink-100 transition-colors"
            aria-label="返回"
          >
            <ArrowLeft className="w-5 h-5 text-ink-600" />
          </button>
          <h1 className="text-xl font-bold text-ink-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-gold-500" strokeWidth={2} />
            消息详情
          </h1>
          {/* 删除本条 */}
          {item && (
            <button
              onClick={onDelete}
              className="ml-auto grid place-items-center w-9 h-9 rounded-full text-ink-400 hover:text-red-500 hover:bg-ink-100 transition-colors"
              aria-label="删除"
              title="删除"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-ink-400">加载中…</div>
        ) : !item ? (
          <div className="py-16 text-center text-sm text-ink-400">
            未找到这条消息
            <div className="mt-4">
              <button onClick={goBack} className="text-gold-600 font-medium">返回消息列表</button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-card border border-ink-300/40 overflow-hidden">
            {/* 正文 */}
            <div className="px-6 py-5">
              <div className="text-lg font-bold text-ink-900 leading-snug">{item.title}</div>
              <div className="text-[11px] text-ink-400 mt-2">{formatTime(item.created_at)}</div>
              {payload.content_preview ? (
                <div className="mt-4 text-sm text-ink-700 leading-relaxed break-words bg-paper rounded-xl px-4 py-3">
                  {String(payload.content_preview)}
                </div>
              ) : null}
            </div>

            {/* 可选入口区 */}
            <div className="px-6 pb-5 space-y-2.5">
              {/* app 类：引导回 Studio */}
              {isApp && (
                <div className="flex items-start gap-2.5 bg-gold-50 rounded-xl px-4 py-3">
                  <Smartphone className="w-4 h-4 text-gold-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-ink-700 leading-relaxed">
                    请打开 <span className="font-medium">HiPiiDo Studio</span> {appHint}
                  </div>
                </div>
              )}

              {/* 有作品 → 查看作品入口 */}
              {projectShortId && (
                <button
                  onClick={() => navigate(`/p/${projectShortId}`)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-ink-300/40 hover:bg-paper transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm text-ink-800">
                    <ExternalLink className="w-4 h-4 text-gold-500" />
                    查看作品
                  </span>
                  <span className="text-ink-300">›</span>
                </button>
              )}

              {/* 评论/点赞/关注：显示操作人（仅告知，名字本身不可点；想看主页可从作品页进） */}
              {(payload.commenter_nickname || payload.liker_nickname || payload.follower_nickname) ? (
                <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-ink-300/30">
                  <User className="w-4 h-4 text-ink-400 shrink-0" />
                  <div className="text-sm text-ink-600">
                    来自：
                    <span className="text-ink-900 font-medium">
                      {String(payload.commenter_nickname || payload.liker_nickname || payload.follower_nickname)}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function formatTime(ts: number): string {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  return new Date(ts).toLocaleString('zh-CN');
}
